
import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { 
  Card, 
  Button, 
  Row, 
  Col, 
  Badge,
  Table,
  Spinner,
  Alert
} from 'react-bootstrap';
import { fetchStreams } from '../../store/actions/StreamActions';
import SocialStreamingConfig from '../components/Streams/SocialStreamingConfig';

const SocialStreaming = ({ streams, loading, error, fetchStreams }) => {
  const navigate = useNavigate();
  const [selectedStream, setSelectedStream] = useState(null);

  useEffect(() => {
    fetchStreams();
  }, [fetchStreams]);

  const handleConfigureStream = (streamId) => {
    // Navigate to stream detail with social tab
    navigate(`/streams/${streamId}?tab=social`);
  };

  // Check if any stream has social platforms configured
  const hasSocialConfig = (stream) => {
    return stream.youtube_enabled || stream.facebook_enabled || stream.twitch_enabled;
  };

  if (loading && (!streams || streams.length === 0)) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
        <p className="mt-2">Loading streams...</p>
      </div>
    );
  }

  return (
    <>
      <div className="form-head d-flex mb-3 mb-md-4 align-items-start">
        <div className="me-auto d-none d-lg-block">
          <h3 className="text-black font-w600">Social Streaming</h3>
          <p className="mb-0">Configure streaming to YouTube, Facebook, and Twitch</p>
        </div>
      </div>

      {error && (
        <Alert variant="danger" dismissible className="mb-4">
          {error}
        </Alert>
      )}

      <Alert variant="info" className="mb-4">
        <i className="fas fa-info-circle me-2"></i>
        <strong>Multi-Platform Broadcasting:</strong> Configure your streams to simultaneously broadcast to YouTube, Facebook, and Twitch. 
        Click "Configure" on any stream to set up social streaming destinations.
      </Alert>

      {(!streams || streams.length === 0) ? (
        <Card>
          <Card.Body className="text-center py-5">
            <h4>No Streams Available</h4>
            <p className="text-muted">Create a stream first to configure social streaming.</p>
            <Button variant="primary" onClick={() => navigate('/create-stream')}>
              <i className="fas fa-plus me-2"></i>
              Create Stream
            </Button>
          </Card.Body>
        </Card>
      ) : (
        <Card>
          <Card.Header>
            <h5 className="mb-0">
              <i className="fas fa-share-alt me-2"></i>
              Your Streams
            </h5>
          </Card.Header>
          <Card.Body>
            <Table responsive hover>
              <thead>
                <tr>
                  <th>Stream Name</th>
                  <th>Status</th>
                  <th>Social Platforms</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {streams.map((stream) => (
                  <tr key={stream.id}>
                    <td>
                      <strong>{stream.name}</strong>
                      <br />
                      <small className="text-muted">{stream.description || 'No description'}</small>
                    </td>
                    <td>
                      {stream.is_live ? (
                        <Badge bg="success">● Live</Badge>
                      ) : stream.status === 'broadcasting' ? (
                        <Badge bg="success">● Broadcasting</Badge>
                      ) : (
                        <Badge bg="secondary">Inactive</Badge>
                      )}
                    </td>
                    <td>
                      <div className="d-flex gap-2">
                        {stream.youtube_enabled ? (
                          <Badge bg="danger" title="YouTube Enabled">YouTube</Badge>
                        ) : (
                          <Badge bg="light" text="dark" title="YouTube Disabled">YouTube</Badge>
                        )}
                        {stream.facebook_enabled ? (
                          <Badge bg="primary" title="Facebook Enabled">Facebook</Badge>
                        ) : (
                          <Badge bg="light" text="dark" title="Facebook Disabled">Facebook</Badge>
                        )}
                        {stream.twitch_enabled ? (
                          <Badge bg="purple" title="Twitch Enabled" style={{backgroundColor: '#9146FF'}}>Twitch</Badge>
                        ) : (
                          <Badge bg="light" text="dark" title="Twitch Disabled">Twitch</Badge>
                        )}
                      </div>
                    </td>
                    <td>
                      <Button 
                        variant={hasSocialConfig(stream) ? "primary" : "outline-primary"}
                        size="sm"
                        onClick={() => handleConfigureStream(stream.id)}
                      >
                        <i className="fas fa-cog me-1"></i>
                        {hasSocialConfig(stream) ? 'Edit Config' : 'Configure'}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Card.Body>
        </Card>
      )}

      <Card className="mt-4">
        <Card.Header>
          <h5 className="mb-0">
            <i className="fas fa-question-circle me-2"></i>
            How to Configure Social Streaming
          </h5>
        </Card.Header>
        <Card.Body>
          <Row>
            <Col md={4}>
              <div className="text-center mb-3">
                <div className="bg-primary text-white rounded-circle d-inline-flex align-items-center justify-content-center" style={{ width: '40px', height: '40px' }}>
                  1
                </div>
                <h6 className="mt-2">Get Stream Keys</h6>
                <p className="text-muted small">
                  Obtain RTMP URLs and stream keys from YouTube Live, Facebook Live, or Twitch.
                </p>
              </div>
            </Col>
            <Col md={4}>
              <div className="text-center mb-3">
                <div className="bg-primary text-white rounded-circle d-inline-flex align-items-center justify-content-center" style={{ width: '40px', height: '40px' }}>
                  2
                </div>
                <h6 className="mt-2">Configure Platforms</h6>
                <p className="text-muted small">
                  Click "Configure" on your stream and enter the RTMP URL and stream key for each platform.
                </p>
              </div>
            </Col>
            <Col md={4}>
              <div className="text-center mb-3">
                <div className="bg-primary text-white rounded-circle d-inline-flex align-items-center justify-content-center" style={{ width: '40px', height: '40px' }}>
                  3
                </div>
                <h6 className="mt-2">Start Broadcasting</h6>
                <p className="text-muted small">
                  Start streaming to your RTMP URL. Your broadcast will automatically be pushed to all enabled platforms.
                </p>
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>
    </>
  );
};

const mapStateToProps = (state) => ({
  streams: state.streams.streams,
  loading: state.streams.loading,
  error: state.streams.error
});

const mapDispatchToProps = {
  fetchStreams
};

export default connect(mapStateToProps, mapDispatchToProps)(SocialStreaming);


